import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { landingPath, useSession } from './lib/session';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { CompanyPage } from './pages/CompanyPage';
import { ProjectPage } from './pages/ProjectPage';
import { BoardPage } from './pages/BoardPage';
import { ProjectManagementPage } from './pages/ProjectManagementPage';
import { InvitationsPage } from './pages/InvitationsPage';
import { RoadmapRoute } from './pages/RoadmapRoute';
import { ReportsPage } from './pages/ReportsPage';
import { TimesheetPage } from './pages/TimesheetPage';
import { RolesPage } from './pages/RolesPage';
import { AuditPage } from './pages/AuditPage';
import { SearchPage } from './pages/SearchPage';
import { MyWorkPage } from './pages/MyWorkPage';
import { PeoplePage } from './pages/PeoplePage';
import { WorkPage } from './pages/WorkPage';
import { ReleasesPage } from './pages/ReleasesPage';
import { CompanyOverviewPage } from './pages/CompanyOverviewPage';
import { ReportRoute } from './pages/ReportRoute';
import { RaiseRequestPage } from './pages/RaiseRequestPage';
import { IncidentsPage } from './pages/IncidentsPage';
import { ProblemsPage } from './pages/ProblemsPage';
import { ChangesPage } from './pages/ChangesPage';
import { SettingsPage } from './pages/SettingsPage';

/** Nothing renders behind the gate until the session call has come back. */
function RequireSession({ children }: { children: ReactNode }) {
  const { session, loading, error } = useSession();
  const location = useLocation();

  if (loading) return <div className="empty">Loading…</div>;
  // An API fault is shown rather than bounced to the login page, which would just fail again.
  if (!session && error) {
    return (
      <div className="main-inner">
        <div className="callout" role="alert">
          <strong>The Hub could not load your session.</strong>
          <div style={{ marginTop: 4 }}>{error}</div>
        </div>
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;

  // Inside the shell, so a page that throws leaves the navigation working. Keyed on the path
  // so moving to another page clears it.
  return (
    <Layout>
      <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
    </Layout>
  );
}

/** Sends someone to their own company when they have exactly one. */
function Landing() {
  const { session, loading } = useSession();
  if (loading) return <div className="empty">Loading…</div>;
  if (!session) return <Navigate to="/login" replace />;
  return <Navigate to={landingPath(session)} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/companies"
        element={
          <RequireSession>
            <CompaniesPage />
          </RequireSession>
        }
      />
      <Route
        path="/companies/:companyId"
        element={
          <RequireSession>
            <CompanyPage />
          </RequireSession>
        }
      />
      <Route
        path="/companies/:companyId/request"
        element={
          <RequireSession>
            <RaiseRequestPage />
          </RequireSession>
        }
      />
      {/*
        Sprints were retired on 2026-10-08. A bookmark to the old planning page lands on project
        management rather than a blank page, which is where that work is planned now.
      */}
      <Route path="/companies/:companyId/sprints" element={<Navigate to="/project-management" replace />} />
      <Route path="/sprints" element={<Navigate to="/project-management" replace />} />
      <Route
        path="/companies/:companyId/incidents"
        element={
          <RequireSession>
            <IncidentsPage />
          </RequireSession>
        }
      />
      <Route
        path="/incidents"
        element={
          <RequireSession>
            <IncidentsPage />
          </RequireSession>
        }
      />
      <Route
        path="/companies/:companyId/problems"
        element={
          <RequireSession>
            <ProblemsPage />
          </RequireSession>
        }
      />
      <Route
        path="/problems"
        element={
          <RequireSession>
            <ProblemsPage />
          </RequireSession>
        }
      />
      <Route
        path="/projects/:projectId/changes"
        element={
          <RequireSession>
            <ChangesPage />
          </RequireSession>
        }
      />
      <Route
        path="/changes"
        element={
          <RequireSession>
            <ChangesPage />
          </RequireSession>
        }
      />
      {/*
        Priorities, Issues and Bugs became one page, Work. Their old addresses still land there,
        a company's issues on that company and Bugs on the Bugs lane — bookmarks and emails
        with the old links keep working.
      */}
      <Route path="/companies/:companyId/issues" element={<CompanyWorkRedirect />} />
      {/* From the sidebar, with no company named: it settles on one and corrects the URL. */}
      <Route
        path="/report"
        element={
          <RequireSession>
            <ReportRoute />
          </RequireSession>
        }
      />
      <Route path="/bugs" element={<Navigate to="/work?lane=bugs" replace />} />
      <Route path="/issues" element={<Navigate to="/work" replace />} />
      {/*
        Where an invitation is answered — from the bell, from the email, or typed.
        No token in the address: the answer is given in a signed-in session and matched on the
        person's own email, so a forwarded link grants nobody anything.
      */}
      <Route
        path="/invitations"
        element={
          <RequireSession>
            <InvitationsPage />
          </RequireSession>
        }
      />

      {/* Every project somebody can reach, with its board under the picker. */}
      <Route
        path="/project-management"
        element={
          <RequireSession>
            <ProjectManagementPage />
          </RequireSession>
        }
      />

      <Route
        path="/projects/:projectId"
        element={
          <RequireSession>
            <ProjectPage />
          </RequireSession>
        }
      />

      <Route
        path="/projects/:projectId/board"
        element={
          <RequireSession>
            <BoardPage />
          </RequireSession>
        }
      />
      <Route
        path="/projects/:projectId/roadmap"
        element={
          <RequireSession>
            <RoadmapRoute />
          </RequireSession>
        }
      />
      <Route
        path="/my-work"
        element={
          <RequireSession>
            <MyWorkPage />
          </RequireSession>
        }
      />
      <Route
        path="/work"
        element={
          <RequireSession>
            <WorkPage />
          </RequireSession>
        }
      />
      <Route path="/priorities" element={<Navigate to="/work" replace />} />
      {/* Management's view of one company: health, timeline, every task, and a board deck. */}
      {['/overview', '/companies/:companyId/overview'].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireSession>
              <CompanyOverviewPage />
            </RequireSession>
          }
        />
      ))}
      {/* What went live: the list, one release, and the editor — `/releases/new`, `/releases/:id/edit`. */}
      {['/releases', '/releases/:releaseId', '/releases/:releaseId/:mode'].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireSession>
              <ReleasesPage />
            </RequireSession>
          }
        />
      ))}
      <Route
        path="/timesheets"
        element={
          <RequireSession>
            <TimesheetPage />
          </RequireSession>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireSession>
            <ReportsPage />
          </RequireSession>
        }
      />
      <Route
        path="/search"
        element={
          <RequireSession>
            <SearchPage />
          </RequireSession>
        }
      />
      <Route
        path="/roles"
        element={
          <RequireSession>
            <RolesPage />
          </RequireSession>
        }
      />
      <Route
        path="/audit"
        element={
          <RequireSession>
            <AuditPage />
          </RequireSession>
        }
      />
      <Route
        path="/people"
        element={
          <RequireSession>
            <PeoplePage />
          </RequireSession>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireSession>
            <SettingsPage />
          </RequireSession>
        }
      />
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

/** `/companies/:id/issues`, from before Work replaced it: the same company's work there. */
function CompanyWorkRedirect() {
  const { companyId } = useParams<{ companyId: string }>();
  return <Navigate to={companyId ? `/work?company=${companyId}` : '/work'} replace />;
}
